import { Box, Grid, Typography }                                          from '@mui/material';
import qs                                                                 from 'qs';
import { FC, useContext, useEffect, useMemo, useState }                   from 'react';
import { useLocation, useNavigate }                                       from 'react-router-dom';
import { AppTrackingTransparency }                                        from 'ios-tracking-transparency';
import { RateApp }                                                        from 'capacitor-rate-app';
import styled                                                             from 'styled-components';
import { ProductFruits }                                                  from 'react-product-fruits';
import { Layout }                                                         from '../../Layout/Layout';
import { PageBody }                                                       from '../../shared/components';
import { Notistack }                                                      from '../../shared/components/Notistack/Notistack';
import { useFetchSelectedFamily }                                         from '../../shared/components/SelectFamily/queries';
import { SelectFamily }                                                   from '../../shared/components/SelectFamily/SelectFamily';
import { isAdminRoles }                                                   from '../../shared/functions';
import { useFetchFamilies, useFetchFamilyMembers, useUpdateUserPlatform } from '../../shared/queries/family';
import { ResponsivePageTitle }                                            from '../../shared/styledComponents';
import { capitalizeFirstLetter }                                          from '../../shared/utils/commonFunctions';
import { isAndroid, isIOS, isNative }                                     from '../../shared/utils/platform';
import AuthorizationContext                                               from '../../shared/utils/withAuthorization/authorizationContext';
import { userPlatform }                                                   from '../../shared/constants/constants';
import { CHANGE_PIN_ROUTE }                                               from '../Settings/components/ManageFamilyAccount/ManageFamilyAccount';
import { CHANGE_MEMBER_PIN_ROUTE }                                        from '../Settings/components/ManageMemberAccount/ManageMemberAccount';
import { AddToFamilyCore }                                                from './components/FamilyDocuments/AddToFamilyCore';
import { FamilyMembers, FamilySection }                                   from './components/FamilyMembers/FamilyMembers';
import { FamilyMembersList }                                              from './components/FamilyMembers/FamilyMembersList';
import { MembersActivity }                                                from './components/FamilyTabs/MembersActivity';
import { IFamily }                                                        from '../../Auth/shared/interfaces';

const DropdownSpacing = styled.div`
  margin-bottom: 36px !important;
`;

export const DashboardResponsivePageTitle = styled(ResponsivePageTitle)`
  gap: 0px;
`;

const AddToFamilyCoreSection = styled(FamilySection)`
  background-color: #fac05e;
`;

const DashboardHeader = styled(Typography)`
  font-size: 18px !important;
  font-weight: 600 !important;
  letter-spacing: 1.13px !important;
`;

export const Dashboard: FC = () => {
  const history = useNavigate();
  const { search } = useLocation();
  const params: any = qs.parse(search, { ignoreQueryPrefix: true });
  const { showTipsModal } = params;

  const localUserPlatform = localStorage.getItem(userPlatform);

  const [ platform, setPlatform ] = useState('');

  const currentUser = useContext(AuthorizationContext);
  const { data: families, isLoading: loadingFamilies, isError: familiesError, refetch: familiesRefetch } = useFetchFamilies();
  const { mutate: updatePlatform } = useUpdateUserPlatform();
  const { data: family, isLoading: loadingFamily, isError: familyError, refetch: familyRefetch } = useFetchSelectedFamily();
  const { data: familyMembers, isLoading: loadingMembers } = useFetchFamilyMembers(family?.id, { enabled: !!family?.id });
  const familiesList = families?.map(family => family.name) || [];
  const isAdminRole = isAdminRoles(currentUser?.data?.accountType);

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const matchingFamily = families?.find((userFamily: IFamily) => userFamily.id === family?.id);
  const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;

  const setSelectedMember = (id?: string) => {
    if (params.id !== id) {
      history({ search: qs.stringify({ ...params, id }) });
    }
  };

  const addDeviceToHash = (device: string) => {
    const hash = window.location.hash;
    const [path, queryString = ''] = hash.slice(1).split('?');

    const params = new URLSearchParams(queryString);
    params.set('device', device);

    const newHash = `#${path}?${params.toString()}`;
    window.history.replaceState({}, '', newHash);
  };

  const getUserPlatform = () => {
    if (isIOS || isMac) {
      setPlatform('ios');
      addDeviceToHash('ios');
      AppTrackingTransparency.trackingAuthorizationStatus()
        .then((trackStat) => {
          if (trackStat.status !== 'authorized') {
            AppTrackingTransparency.requestTrackingAuthorization().then((response) => {
              // Handle the tracking authorization request response
            });
          }
        })
        .catch((error) => {
          // Handle any error that occurs while checking the status
          console.error('Error checking tracking authorization status:', error);
        });
    } else if (isAndroid || !isMac) {
      setPlatform('android');
      addDeviceToHash('android');
    } else {
      setPlatform('desktop');
      addDeviceToHash('web');
    }
  };

  const updatePlatformAndSetLocalStorage = async () => {
    if (platform && !localUserPlatform) {
      try {
        await updatePlatform( platform );
        localStorage.setItem(userPlatform, platform);
      } catch (error) {
        console.error("Failed to update platform", error);
      }
    }
  };

  const shouldShowReview = (): boolean => {
    const lastReviewPrompt = localStorage.getItem('lastReviewPrompt');
    const now = Date.now();

    if (!lastReviewPrompt || now - parseInt(lastReviewPrompt, 10) > TWO_WEEKS_IN_MS) {
      localStorage.setItem('lastReviewPrompt', now.toString());
      return true;
    }
    return false;
  };

  const requestReview = async () => {
    if (shouldShowReview() && isNative) {
      try {
        await RateApp.requestReview();
        console.log("Review dialog shown successfully");
      } catch (error) {
        console.error("Error showing review dialog:", error);
      }
    } else {
      console.log("Not the right time to request a review");
    }
  };

  useEffect(() => {
    //familiesRefetch();
    familyRefetch();
    getUserPlatform();
    requestReview();
  }, []);

  useEffect(() => {
    updatePlatformAndSetLocalStorage();
  }, [ platform, localUserPlatform ]);

  useEffect(() => {
    if (currentUser?.data?.id && (!params.id || params.id !== currentUser?.data?.id)) {
      setSelectedMember(currentUser?.data?.id);
    }

    if (!showTipsModal && currentUser?.data?.hasPin === false) {
      Notistack.enqueueSnackbar('Your should set a PIN.', 'success');

      history({
        pathname : isAdminRole ? CHANGE_PIN_ROUTE : CHANGE_MEMBER_PIN_ROUTE,
        search   : qs.stringify({
          goBack: true,
        }),
      });
    }
  }, [ currentUser ]);

  useEffect(() => {
    getUserPlatform();
  }, []);

  const DashboardPage = (
    <>
      {currentUser?.data?.id && (
        <ProductFruits
          workspaceCode="M8ZYkfvyyV9WxUa4"
          language="en"
          // lifeCycle="unmount"
          user={{
            username  : currentUser.data.id,
            firstname : currentUser.data.firstName || "not provided",
            lastname  : currentUser.data.lastName.trim() || "not provided",
          }}
        />
      )}

      <DashboardResponsivePageTitle>
        Hi { capitalizeFirstLetter(currentUser?.data?.firstName) }, Welcome Back!
      </DashboardResponsivePageTitle>

      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          xs={12}
          sm={6}
          lg={7}
        >
          <FamilyMembers
            user={currentUser?.data}
            loading={loadingMembers}
            data={familyMembers}
            name={family?.name}
            isActivePayment={matchingFamily?.isActivePayment}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          lg={5}
        >
          <AddToFamilyCoreSection>
            <DashboardHeader variant="h5">Add To Family Core</DashboardHeader>

            <AddToFamilyCore
              familyId={family?.id}
              isActivePayment={matchingFamily?.isActivePayment}
            />
          </AddToFamilyCoreSection>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <PageBody>
            { familiesList.length > 1 && (
              <Grid
                item
                xs={12}
                sm={8}
                md={4}
              >
                <DropdownSpacing>
                  <SelectFamily />
                </DropdownSpacing>
              </Grid>
            ) }

            <Grid
              item
              xs={12}
            >
              <FamilyMembersList
                shaded
                disabledForMembers
                data={familyMembers}
                loading={loadingMembers}
                selected={params.id as string}
                onSelect={(id) => setSelectedMember(id)}
              />

              <Box height={36} />

              { (!params?.id || familyMembers?.find(i => i.id === params?.id)) && (
                <MembersActivity />
              ) }
            </Grid>
          </PageBody>
        </Grid>
      </Grid>
    </>
  );

  // If currentUser has an error or no data after loading, don't show loading spinner
  const shouldShowLoading = (loadingFamilies || loadingFamily || loadingMembers || currentUser?.isLoading)
    && !currentUser?.isError;

  return useMemo(() =>
    (<Layout loading={shouldShowLoading}>
      { DashboardPage }
    </Layout>), [ loadingMembers, loadingFamily, loadingFamilies, currentUser?.isLoading, currentUser?.isError, search, familyMembers, matchingFamily ]
  );
};
